<template>
  <div class="role-form-wrapper">
    <!-- Toast Notifications -->
    <div class="toast-container">
      <CToaster :autohide="3000">
        <CToast
          v-for="info in infoList"
          :key="info.id"
          :show="true"
          :header="info.header"
          :color="info.color"
        >
          {{ info.message }}
        </CToast>
      </CToaster>
    </div>

    <!-- Page Header -->
    <CRow class="mb-4">
      <CCol>
        <div class="page-header">
          <h4 class="mb-0">
            <i class="fas fa-user-shield me-2 text-primary"></i>
            <strong>{{ obj.name || 'Role' }} Permissions</strong>
          </h4>
          <small class="text-muted">Configure module and submodule access permissions for this role</small>
        </div>
      </CCol>
    </CRow>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="text-center py-5">
        <CSpinner color="primary" />
        <p class="text-muted mt-3">Loading role permissions...</p>
      </div>
    </div>

    <!-- Role Permissions Form -->
    <CRow v-else>
      <CCol sm="12">
        <CCard class="form-card">
          <CCardHeader class="form-header">
            <div class="d-flex align-items-center justify-content-between">
              <div>
                <i class="fas fa-list-check me-2 text-primary"></i>
                <strong>Module & SubModule Permissions</strong>
              </div>
              <CBadge color="info">
                {{ moduleItems.length }} Modules, {{ subModuleCount }} SubModules
              </CBadge>
            </div>
          </CCardHeader>
          <CCardBody>
            <CForm>
              <!-- Role Basic Information -->
              <CRow class="mb-4">
                <CCol md="6">
                  <CInput
                    label="System Name"
                    placeholder="Enter system name (e.g., StockManager)"
                    v-model="obj.name"
                    required
                    description="Used internally by the system (no spaces)"
                  />
                </CCol>
                <CCol md="6">
                  <CInput
                    label="Display Name"
                    placeholder="Enter display name (e.g., Stock Manager)"
                    v-model="obj.displayName"
                    required
                    description="Shown to users in the interface"
                  />
                </CCol>
              </CRow>
              <CRow class="mb-4">
                <CCol md="12">
                  <CTextarea
                    label="Description"
                    placeholder="Enter role description (optional)"
                    v-model="obj.description"
                    rows="2"
                  />
                </CCol>
              </CRow>

              <!-- Permissions Info Alert -->
              <CAlert color="info" :show="true" class="mb-4">
                <i class="fas fa-info-circle me-2"></i>
                <strong>Permission Management:</strong> Configure access permissions for modules and their submodules.
                Click on a module to expand/collapse its submodules.
              </CAlert>

              <!-- Permissions Table -->
              <div class="permissions-table-wrapper">
                <table class="table table-hover table-bordered">
                  <thead class="table-light">
                    <tr>
                      <th style="width: 40%">Module / SubModule Name</th>
                      <th class="text-center" style="width: 15%">
                        <i class="fas fa-eye me-1"></i>View
                      </th>
                      <th class="text-center" style="width: 15%">
                        <i class="fas fa-plus me-1"></i>Add
                      </th>
                      <th class="text-center" style="width: 15%">
                        <i class="fas fa-edit me-1"></i>Update
                      </th>
                      <th class="text-center" style="width: 15%">
                        <i class="fas fa-trash me-1"></i>Delete
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="(module, index) in moduleItems">
                      <!-- Module Row -->
                      <tr :key="`module-${module.id}`" class="module-row">
                        <td>
                          <div class="d-flex align-items-center">
                            <button
                              v-if="hasSubModules(module)"
                              type="button"
                              class="btn btn-sm btn-link text-primary p-1 me-2"
                              @click.prevent="toggleModule(module.id)"
                              style="text-decoration: none; font-size: 1rem; line-height: 1; min-width: 20px;"
                            >
                              <span v-if="module.expanded">▼</span>
                              <span v-else>▶</span>
                            </button>
                            <span v-else style="width: 28px; display: inline-block;"></span>
                            <i class="fas fa-puzzle-piece me-2 text-primary"></i>
                            <strong>{{ module.name }}</strong>
                            <CBadge v-if="module.subModules && module.subModules.length" color="secondary" class="ms-2">
                              {{ module.subModules.length }} sub-modules
                            </CBadge>
                          </div>
                        </td>
                        <td class="text-center">
                          <CSwitch
                            color="success"
                            :checked.sync="module.canView"
                            label-on="YES"
                            label-off="NO"
                            size="sm"
                          />
                        </td>
                        <td class="text-center">
                          <CSwitch
                            color="primary"
                            :checked.sync="module.canAdd"
                            label-on="YES"
                            label-off="NO"
                            size="sm"
                          />
                        </td>
                        <td class="text-center">
                          <CSwitch
                            color="warning"
                            :checked.sync="module.canUpdate"
                            label-on="YES"
                            label-off="NO"
                            size="sm"
                          />
                        </td>
                        <td class="text-center">
                          <CSwitch
                            color="danger"
                            :checked.sync="module.canDelete"
                            label-on="YES"
                            label-off="NO"
                            size="sm"
                          />
                        </td>
                      </tr>

                      <!-- SubModule Rows -->
                      <template v-if="module.expanded && module.subModules && module.subModules.length">
                        <tr
                          v-for="subModule in module.subModules"
                          :key="`submodule-${subModule.id}`"
                          class="submodule-row"
                        >
                          <td>
                            <div class="d-flex align-items-center ps-5">
                              <i class="fas fa-arrow-right me-2 text-muted"></i>
                              <span>{{ subModule.name }}</span>
                            </div>
                          </td>
                          <td class="text-center">
                            <CSwitch
                              color="success"
                              :checked.sync="subModule.canView"
                              label-on="YES"
                              label-off="NO"
                              size="sm"
                            />
                          </td>
                          <td class="text-center">
                            <CSwitch
                              v-if="shouldShowAddPermission(subModule)"
                              color="primary"
                              :checked.sync="subModule.canAdd"
                              label-on="YES"
                              label-off="NO"
                              size="sm"
                            />
                            <span v-else class="text-muted">
                              <i class="fas fa-minus"></i>
                            </span>
                          </td>
                          <td class="text-center">
                            <CSwitch
                              color="warning"
                              :checked.sync="subModule.canUpdate"
                              label-on="YES"
                              label-off="NO"
                              size="sm"
                            />
                          </td>
                          <td class="text-center">
                            <CSwitch
                              v-if="shouldShowDeletePermission(subModule)"
                              color="danger"
                              :checked.sync="subModule.canDelete"
                              label-on="YES"
                              label-off="NO"
                              size="sm"
                            />
                            <span v-else class="text-muted">
                              <i class="fas fa-minus"></i>
                            </span>
                          </td>
                        </tr>
                      </template>
                    </template>
                  </tbody>
                </table>
              </div>
            </CForm>
          </CCardBody>

          <!-- Role Permissions JSON Section (For Troubleshooting) -->
          <CCard class="mt-4 mx-3 mb-3">
            <CCardHeader>
              <strong>
                <i class="fas fa-code me-2"></i>
                Role Permissions JSON (For Troubleshooting)
              </strong>
            </CCardHeader>
            <CCardBody>
              <CTextarea
                :value="permissionsJson"
                rows="20"
                readonly
                style="font-family: monospace; font-size: 12px;"
              />
              <CButton
                color="info"
                size="sm"
                class="mt-2"
                @click="copyPermissionsJson"
              >
                <i class="fas fa-copy me-1"></i>
                Copy to Clipboard
              </CButton>
            </CCardBody>
          </CCard>

          <!-- Form Footer -->
          <CCardFooter class="form-footer">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <CButton
                  color="primary"
                  :disabled="submitted"
                  @click="submit"
                >
                  <CSpinner v-if="submitted" size="sm" class="me-2" />
                  <i v-else class="fas fa-check-circle me-2"></i>
                  {{ submitted ? 'Saving...' : 'Submit' }}
                </CButton>
                <CButton
                  color="secondary"
                  class="ms-2"
                  @click="cancel"
                  :disabled="submitted"
                >
                  <i class="fas fa-times me-2"></i>
                  Cancel
                </CButton>
              </div>
              <div class="text-muted">
                <small>
                  <i class="fas fa-clock me-1"></i>
                  Changes will take effect immediately upon save
                </small>
              </div>
            </div>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import RoleApi from "../../lib/roleApi";

export default {
  name: "Role",
  data: () => {
    return {
      infoList: [],
      api: new RoleApi(),
      obj: {},
      loading: false,
      submitted: false,
      moduleItems: [],
    };
  },
  mounted() {
    this.resetObj();
  },
  computed: {
    subModuleCount() {
      return this.moduleItems.reduce((count, module) => {
        return count + (module.subModules ? module.subModules.length : 0);
      }, 0);
    },
    permissionsJson() {
      return JSON.stringify({
        roleModulePermissions: this.moduleItems.map(m => ({
          moduleId: m.id,
          moduleName: m.name,
          moduleCode: m.code,
          canView: m.canView,
          canAdd: m.canAdd,
          canUpdate: m.canUpdate,
          canDelete: m.canDelete
        })),
        roleSubModulePermissions: this.moduleItems.flatMap(m =>
          (m.subModules || []).map(sm => ({
            subModuleId: sm.id,
            subModuleName: sm.name,
            subModuleCode: sm.code,
            moduleId: m.id,
            moduleName: m.name,
            moduleCode: m.code,
            canView: sm.canView,
            canAdd: sm.canAdd,
            canUpdate: sm.canUpdate,
            canDelete: sm.canDelete
          }))
        )
      }, null, 2); // Pretty print with 2-space indentation
    },
  },
  methods: {
    hasSubModules(module) {
      return !!(module.subModules && Array.isArray(module.subModules) && module.subModules.length > 0);
    },

    toggleModule(moduleId) {
      const module = this.moduleItems.find(m => m.id === moduleId);
      if (module) {
        // Use Vue.set to ensure reactivity
        this.$set(module, 'expanded', !module.expanded);
      }
    },

    // Determine if a permission type should be shown for a submodule
    shouldShowPermission(subModule, permissionType) {
      // List of Stock sub-module codes that only allow View and Update
      const restrictedSubModules = [
        'STOCK_INFO',
        'VEHICLE_INFO',
        'PURCHASE',
        'IMPORT',
        'CLEARANCE',
        'SALE',
        'PRICING',
        'ARRIVAL_CHECKLIST',
        'REGISTRATION',
        'EXPENSES',
        'ADMINISTRATIVE_COST',
        'DISBURSEMENT',
        'STOCK_ADVERTISEMENT'  // Advertisement under Stocks module
      ];

      // If this is a restricted submodule, only show View and Update
      if (restrictedSubModules.includes(subModule.code)) {
        return permissionType === 'view' || permissionType === 'update';
      }

      // For all other submodules, show all 4 permission types
      return true;
    },

    // Helper to check if Add permission should be shown for a submodule
    shouldShowAddPermission(subModule) {
      return this.shouldShowPermission(subModule, 'add');
    },

    // Helper to check if Delete permission should be shown for a submodule
    shouldShowDeletePermission(subModule) {
      return this.shouldShowPermission(subModule, 'delete');
    },

    onSubmit() {
      // List of restricted submodule codes (same as in shouldShowPermission)
      const restrictedSubModules = [
        'STOCK_INFO',
        'VEHICLE_INFO',
        'PURCHASE',
        'IMPORT',
        'CLEARANCE',
        'SALE',
        'PRICING',
        'ARRIVAL_CHECKLIST',
        'REGISTRATION',
        'EXPENSES',
        'ADMINISTRATIVE_COST',
        'DISBURSEMENT',
        'STOCK_ADVERTISEMENT'
      ];

      // Separate module and submodule permissions
      const roleModulePermissions = this.moduleItems.map(module => ({
        ...(module.permissionId && { id: module.permissionId }), // Only include id if it exists
        moduleId: module.id,
        roleId: this.obj.id,
        canView: module.canView || false,
        canAdd: module.canAdd || false,
        canUpdate: module.canUpdate || false,
        canDelete: module.canDelete || false,
      }));

      const roleSubModulePermissions = [];
      this.moduleItems.forEach(module => {
        if (module.subModules) {
          module.subModules.forEach(subModule => {
            // Check if this is a restricted submodule
            const isRestricted = restrictedSubModules.includes(subModule.code);

            roleSubModulePermissions.push({
              ...(subModule.permissionId && { id: subModule.permissionId }), // Only include id if it exists
              subModuleId: subModule.id,
              roleId: this.obj.id,
              canView: subModule.canView || false,
              // Force canAdd and canDelete to false for restricted submodules
              canAdd: isRestricted ? false : (subModule.canAdd || false),
              canUpdate: subModule.canUpdate || false,
              canDelete: isRestricted ? false : (subModule.canDelete || false),
            });
          });
        }
      });

      this.obj.roleModulePermissions = roleModulePermissions;
      this.obj.roleSubModulePermissions = roleSubModulePermissions;

      if (!this.obj.id) {
        this.api
          .create(this.obj)
          .then((response) => {
            this.toast("Success", "Role permissions created successfully", "success");
            // REMOVED REDIRECT: Stay on current page after save (STANDARD RULE)
            this.submitted = false;
          })
          .catch(({ data }) => {
            this.toast("Error", helper.getErrorMessage(data), "danger");
            this.submitted = false;
          });
      } else {
        this.api
          .update(this.obj)
          .then((response) => {
            this.toast("Success", "Role permissions updated successfully", "success");
            // REMOVED REDIRECT: Stay on current page after save (STANDARD RULE)
            this.submitted = false;
          })
          .catch(({ data }) => {
            this.toast("Error", helper.getErrorMessage(data), "danger");
            this.submitted = false;
          });
      }
    },

    toast(header, message, color) {
      const toastId = `toast-${Date.now()}-${Math.random()}`;
      this.infoList.push({
        id: toastId,
        header: header,
        message: message,
        color: color,
      });

      // Auto-remove toast after autohide duration
      setTimeout(() => {
        const index = this.infoList.findIndex(info => info.id === toastId);
        if (index !== -1) {
          this.infoList.splice(index, 1);
        }
      }, 3500); // 3000ms autohide + 500ms buffer
    },

    resetObj() {
      this.loading = true;
      if (this.$route.params.id) {
        this.api
          .get(this.$route.params.id)
          .then((response) => {
            this.obj = response.result;
            this.processPermissions();
            this.loading = false;
          })
          .catch(({ data }) => {
            this.toast("Error", helper.getErrorMessage(data), "danger");
            this.loading = false;
          });
      } else {
        // Creating a new role - load all modules
        this.obj = this.getEmptyObj();
        this.api
          .getModulesTemplate()
          .then((response) => {
            this.processModulesTemplate(response.result);
            this.loading = false;
          })
          .catch((error) => {
            const errorMessage = error.response?.data
              ? helper.getErrorMessage(error.response.data)
              : "Failed to load modules. Please ensure the API is running.";
            this.toast("Error", errorMessage, "danger");
            this.loading = false;
          });
      }
    },

    processModulesTemplate(modules) {
      // Process modules template for new role creation
      console.log('=== API Response - Raw Modules ===', modules);

      const tempModuleItems = modules.map(module => {
        const subModules = [];

        if (module.subModules && Array.isArray(module.subModules)) {
          console.log(`Module: ${module.name}, Raw SubModules:`, module.subModules.map(s => ({ name: s.name, displayOrder: s.displayOrder })));

          module.subModules.forEach(subMod => {
            subModules.push({
              id: subMod.id,
              name: subMod.name,
              code: subMod.code,
              displayOrder: subMod.displayOrder || 0,
              permissionId: null, // No permission ID yet (new role)
              canView: false,
              canAdd: false,
              canUpdate: false,
              canDelete: false,
            });
          });

          // Sort submodules by displayOrder
          subModules.sort((a, b) => a.displayOrder - b.displayOrder);
          console.log(`Module: ${module.name}, Sorted SubModules:`, subModules.map(s => ({ name: s.name, displayOrder: s.displayOrder })));
        }

        return {
          id: module.id,
          name: module.name,
          code: module.code,
          displayOrder: module.displayOrder || 0,
          permissionId: null, // No permission ID yet (new role)
          canView: false,
          canAdd: false,
          canUpdate: false,
          canDelete: false,
          subModules: subModules,
          expanded: false,
        };
      });

      console.log('=== Before Module Sort ===', tempModuleItems.map(m => ({ name: m.name, displayOrder: m.displayOrder })));

      // Sort modules by displayOrder
      tempModuleItems.sort((a, b) => a.displayOrder - b.displayOrder);

      console.log('=== After Module Sort ===', tempModuleItems.map(m => ({ name: m.name, displayOrder: m.displayOrder })));
      console.log('=== Final moduleItems ===', tempModuleItems);

      // Use Vue.set to replace the entire array to ensure reactivity
      this.$set(this, 'moduleItems', tempModuleItems);
    },

    processPermissions() {
      // Build a map of module permissions
      const modulePerm = {};
      if (this.obj.roleModulePermissions) {
        this.obj.roleModulePermissions.forEach(perm => {
          if (perm.module) {
            modulePerm[perm.module.id] = {
              permissionId: perm.id,
              canView: perm.canView,
              canAdd: perm.canAdd,
              canUpdate: perm.canUpdate,
              canDelete: perm.canDelete,
              module: perm.module,
            };
          }
        });
      }

      // Build a map of submodule permissions
      const subModulePerm = {};
      if (this.obj.roleSubModulePermissions) {
        this.obj.roleSubModulePermissions.forEach(perm => {
          if (perm.subModule) {
            subModulePerm[perm.subModule.id] = {
              permissionId: perm.id,
              canView: perm.canView,
              canAdd: perm.canAdd,
              canUpdate: perm.canUpdate,
              canDelete: perm.canDelete,
            };
          }
        });
      }

      // Build the module items with nested submodules
      const tempModuleItems = Object.values(modulePerm).map(modPerm => {
        const module = modPerm.module;
        const subModules = [];

        if (module.subModules && Array.isArray(module.subModules)) {
          module.subModules.forEach(subMod => {
            const subPermission = subModulePerm[subMod.id] || {};
            subModules.push({
              id: subMod.id,
              name: subMod.name,
              code: subMod.code,
              displayOrder: subMod.displayOrder || 0,
              permissionId: subPermission.permissionId,
              canView: subPermission.canView || false,
              canAdd: subPermission.canAdd || false,
              canUpdate: subPermission.canUpdate || false,
              canDelete: subPermission.canDelete || false,
            });
          });
          // Sort submodules by displayOrder
          subModules.sort((a, b) => a.displayOrder - b.displayOrder);
        }

        return {
          id: module.id,
          name: module.name,
          code: module.code,
          displayOrder: module.displayOrder || 0,
          permissionId: modPerm.permissionId,
          canView: modPerm.canView || false,
          canAdd: modPerm.canAdd || false,
          canUpdate: modPerm.canUpdate || false,
          canDelete: modPerm.canDelete || false,
          subModules: subModules,
          expanded: false,
        };
      });

      // Sort modules by displayOrder
      tempModuleItems.sort((a, b) => a.displayOrder - b.displayOrder);

      // Use Vue.set to replace the entire array to ensure reactivity
      this.$set(this, 'moduleItems', tempModuleItems);
    },

    getEmptyObj() {
      return {
        name: "",
        displayName: "",
        description: "",
        isSystemRole: false,
        roleModulePermissions: [],
        roleSubModulePermissions: [],
      };
    },

    submit() {
      this.onSubmit();
      this.submitted = true;
    },

    cancel() {
      this.$router.push({ path: "/admins/RoleList" });
    },

    copyPermissionsJson() {
      navigator.clipboard.writeText(this.permissionsJson).then(() => {
        this.toast("Success", "Permissions JSON copied to clipboard", "success");
      }).catch(() => {
        this.toast("Error", "Failed to copy to clipboard", "danger");
      });
    },
  },
};
</script>

<style scoped>
/* Professional Container */
.role-form-wrapper {
  padding: 1.5rem;
  min-height: 100vh;
}

/* Toast Notifications */
.toast-container {
  position: fixed;
  top: 1.5rem;
  right: 1.5rem;
  z-index: 1050;
}

/* Page Header */
.page-header {
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 1rem;
}

/* Card Styling */
.form-card {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  border: none;
  border-radius: 0.5rem;
}

.form-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
  border-radius: 0.5rem 0.5rem 0 0 !important;
}

.form-footer {
  background-color: #f8f9fa;
  border-top: 1px solid #e9ecef;
}

/* Permissions Table */
.permissions-table-wrapper {
  overflow-x: auto;
}

.module-row {
  background-color: #f8f9fa;
  font-weight: 500;
}

.module-row:hover {
  background-color: #e9ecef;
}

.submodule-row {
  background-color: #ffffff;
}

.submodule-row:hover {
  background-color: #f8f9fa;
}

.btn-link {
  color: #667eea;
  text-decoration: none;
}

.btn-link:hover {
  color: #764ba2;
}

/* Loading State */
.loading-state {
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
